/**
 * How far a maltster's malt travels — the tier the importer's matcher reads.
 *
 * ## Why the catalog owns this
 *
 * A recipe file that says `Pilsner Malt` and nothing else is naming a *class*,
 * not a product. Something has to choose which of the eighty-six pilsner malts
 * in this package it lands on, and the honest order is: the unbranded class
 * entry first, then a maltster the brewer can plausibly buy from, and never a
 * single-farm malthouse three states away that they have never heard of. The
 * matcher cannot work that out from a name, and it is not the matcher's fact to
 * hold — it is a fact about the *company*, which lives here beside the company's
 * malt.
 *
 * The tier is only ever consulted to break a near-tie on a name that carried no
 * maker at all. A file that names Voyager gets Voyager, wherever Voyager sits.
 *
 * ## What each tier means
 *
 * - **`generic`** — not a company. The `Generic` rows are class entries with
 *   class-typical figures that say so in the preview, which is the right thing
 *   to hand a brewer who named a class.
 * - **`worldwide`** — a homebrew shop on most continents stocks this maltster's
 *   bags, and the brewing world quotes their product names as if they were
 *   generic terms (`Carapils`, `Château Pale Ale`, `Maris Otter`).
 * - **`national`** — sold across a country or a trading bloc, generally through
 *   distributors. A brewer in that country can buy it; a brewer elsewhere
 *   probably cannot. Large industrial maltsters who sell into brewing rather
 *   than onto the homebrew shelf sit here too — Malteurop malts more barley
 *   than anyone in this file and sells almost none of it in 25 kg sacks.
 * - **`regional`** — a craft malthouse selling within a day's drive, or a
 *   single-farm estate. Excellent malt, and never the right answer to a name
 *   that named nobody.
 *
 * The regional list is exactly the makers whose rows live in
 * `data/fermentables/craft/`, plus the three Australian craft houses filed in
 * `australia.ts` — Voyager, Mallokup and AMSAT — who are the same kind of
 * company on the other side of the world. That is not a coincidence: it is the
 * same judgement, written down twice, and `makers.test.ts` checks the halves
 * agree.
 */

/** @see MALTSTER_REACH */
export type MaltsterReach = 'generic' | 'worldwide' | 'national' | 'regional';

/**
 * Every fermentable maker in the catalog, and how far its malt travels.
 *
 * Exhaustive by test: `makers.test.ts` fails if the data grows a maker this map
 * does not name, because a missing maker would silently default to *something*
 * and the default is the one thing a tier must never be.
 */
export const MALTSTER_REACH: Readonly<Record<string, MaltsterReach>> = {
  // Not a company.
  Generic: 'generic',

  // Worldwide.
  Bairds: 'worldwide',
  Bestmalz: 'worldwide',
  Briess: 'worldwide',
  'Castle Malting': 'worldwide',
  Crisp: 'worldwide',
  Dingemans: 'worldwide',
  'Great Western': 'worldwide',
  IREKS: 'worldwide',
  Muntons: 'worldwide',
  Rahr: 'worldwide',
  Simpsons: 'worldwide',
  'The Swaen': 'worldwide',
  'Thomas Fawcett': 'worldwide',
  'Viking Malt': 'worldwide',
  Weyermann: 'worldwide',

  // National.
  'Avangard Malz': 'national',
  'Barrett Burston Malting': 'national',
  'Canada Malting': 'national',
  'French & Jupps': 'national',
  'Gambrinus Malting': 'national',
  Gladfield: 'national',
  'Joe White Maltings': 'national',
  'Malteries Franco-Belges': 'national',
  Malteurop: 'national',
  'Malting Company of Ireland': 'national',
  'Pauls Malt': 'national',
  'Prairie Malt': 'national',
  'Proximity Malt': 'national',
  'Raven Trading': 'national',
  Tchecomalt: 'national',
  'Warminster Maltings': 'national',

  // Regional: the craft malthouses.
  '3D Malt': 'regional',
  'Admiral Maltings': 'regional',
  'AMSAT Character Malts': 'regional',
  'Bear Branch Malt': 'regional',
  'Blacklands Malt': 'regional',
  'Blue Ox Malthouse': 'regional',
  'Byrne Craft Malt': 'regional',
  'Carolina Malt House': 'regional',
  'Cascadia Malts': 'regional',
  'Central Altiplano': 'regional',
  'CNC Malt': 'regional',
  'Cold Stream Malt and Grain': 'regional',
  'Colorado Malting Company': 'regional',
  'Deer Creek Malthouse': 'regional',
  'Double Eagle Malt': 'regional',
  'Eckert Malting': 'regional',
  'Epiphany Craft Malt': 'regional',
  'Gallatin Valley Malt': 'regional',
  'Great Lakes Malting': 'regional',
  'Grouse Malt House': 'regional',
  'Homestead Malting': 'regional',
  'Horton Ridge Malt': 'regional',
  'Hudson Valley Malt': 'regional',
  'Island Malt House': 'regional',
  'LINC Malt': 'regional',
  'Maine Malt House': 'regional',
  'Mainstem Malt': 'regional',
  "Maker's Malt": 'regional',
  'Mallokup Malt': 'regional',
  MaltBroue: 'regional',
  'Malterie Caux-Laflamme': 'regional',
  Maltwerks: 'regional',
  'Maverick Malt House': 'regional',
  'Mecca Grade Estate Malt': 'regional',
  'Mississippi Mills Malting': 'regional',
  'Mitten State Malt': 'regional',
  'Montana Craft Malt': 'regional',
  'Mountain Malt': 'regional',
  'Murmuration Malts': 'regional',
  'Murphy & Rude Malting': 'regional',
  'ND Malting and Hops': 'regional',
  'New York Craft Malt': 'regional',
  'Niagara Malt': 'regional',
  'Origin Malt': 'regional',
  'Rabbit Hill Malthouse': 'regional',
  'Red Shed Malting': 'regional',
  'Riverbend Malt House': 'regional',
  'Root Shoot Malting': 'regional',
  'Rustic Brew Farm': 'regional',
  'Schwebach Malz': 'regional',
  'Scythe Malting': 'regional',
  'Sinagua Malt': 'regional',
  'Skagit Valley Malting': 'regional',
  'Solstice Malt': 'regional',
  'South Fork Malthouse': 'regional',
  'Stone Path Malt': 'regional',
  'Sugar Creek Malt Co.': 'regional',
  TexMalt: 'regional',
  'Thrall Family Malt': 'regional',
  'Troubadour Maltings': 'regional',
  'Two Track Malting': 'regional',
  'Valley Malt': 'regional',
  'Voyager Craft Malt': 'regional',
};

/**
 * How far a named maltster's malt travels.
 *
 * @param maker - The maker exactly as a catalog entry spells it.
 * @returns The tier, or `regional` for a name this map does not carry —
 *   the cautious answer, because the rule that reads this refuses to land a
 *   class name on a regional row and an unknown company should not be handed a
 *   brewer who never asked for it. `makers.test.ts` keeps the fallback
 *   unreachable for the catalog's own data.
 */
export function makerReach(maker: string | null | undefined): MaltsterReach {
  if (maker === null || maker === undefined) return 'regional';
  return MALTSTER_REACH[maker] ?? 'regional';
}
